import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast, Toaster } from 'sonner';
import { 
  BarChart3, Users, FileText, MessageSquare, TrendingUp, 
  Calendar, RefreshCcw, Brain, Mail, CalendarCheck, Unlock
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      const res = await fetch(`${API_URL}/api/admin/analytics?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      } else {
        throw new Error('Failed to load');
      }
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#0066CC', '#00B4A0', '#FF6B2B', '#8B5CF6', '#EC4899'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCcw className="animate-spin text-tech-blue" size={32} />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const leadsByDay = analytics?.leads_by_day || [];
  const leadSources = analytics?.lead_sources || [];
  const bookingStats = analytics?.booking_stats || {};
  const recentActivity = analytics?.recent_activity || {};
  const conversionFunnel = analytics?.conversion_funnel || {};
  const topPages = analytics?.top_pages || [];

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Analytics Dashboard</h1>
          <p className="text-text-secondary mt-1">Track performance, leads, and engagement</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button onClick={loadData} variant="outline">
            <RefreshCcw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary">Total Leads</p>
                <p className="text-2xl font-bold text-text-primary">{summary.total_leads || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-tech-blue/10 flex items-center justify-center">
                <Users className="text-tech-blue" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary">Demo Requests</p>
                <p className="text-2xl font-bold text-text-primary">{summary.demo_requests || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-care-teal/10 flex items-center justify-center">
                <FileText className="text-care-teal" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary">Contacts</p>
                <p className="text-2xl font-bold text-text-primary">{summary.contacts || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-heritage-orange/10 flex items-center justify-center">
                <MessageSquare className="text-heritage-orange" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary">AI Demo Leads</p>
                <p className="text-2xl font-bold text-text-primary">{summary.ai_leads || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain className="text-purple-500" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary">Bookings</p>
                <p className="text-2xl font-bold text-text-primary">{summary.bookings || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CalendarCheck className="text-green-500" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary">Newsletter</p>
                <p className="text-2xl font-bold text-text-primary">{summary.newsletter_subscribers || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
                <Mail className="text-pink-500" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Leads Trend Chart */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="mr-2 text-tech-blue" size={20} />
              Lead Trends
            </CardTitle>
            <CardDescription>Daily leads by source</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={leadsByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
                  <YAxis stroke="#6B7280" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="demos" 
                    stackId="1"
                    stroke="#0066CC" 
                    fill="#0066CC" 
                    fillOpacity={0.6}
                    name="Demo Requests"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="contacts" 
                    stackId="1"
                    stroke="#00B4A0" 
                    fill="#00B4A0" 
                    fillOpacity={0.6}
                    name="Contacts"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="bookings" 
                    stackId="1"
                    stroke="#8B5CF6" 
                    fill="#8B5CF6" 
                    fillOpacity={0.6}
                    name="Bookings"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="ai_leads" 
                    stackId="1"
                    stroke="#FF6B2B" 
                    fill="#FF6B2B" 
                    fillOpacity={0.6}
                    name="AI Demo"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Lead Sources */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="mr-2 text-care-teal" size={20} />
              Lead Sources
            </CardTitle>
            <CardDescription>Where your leads come from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={leadSources.filter(s => s.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {leadSources.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {leadSources.map((source, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: source.color || COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-text-secondary">{source.name}</span>
                    </div>
                    <span className="text-sm font-medium text-text-primary">{source.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Booking Stats */}
      <Card className="border-border-line">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="mr-2 text-tech-blue" size={20} />
            Booking Statistics
          </CardTitle>
          <CardDescription>Strategy call booking performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center p-4 bg-page-bg rounded-lg">
              <div className="text-3xl font-bold text-text-primary">{bookingStats.total || 0}</div>
              <div className="text-sm text-text-secondary">Total Bookings</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{bookingStats.confirmed || 0}</div>
              <div className="text-sm text-green-600">Confirmed</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{bookingStats.completed || 0}</div>
              <div className="text-sm text-blue-600">Completed</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-3xl font-bold text-red-600">{bookingStats.cancelled || 0}</div>
              <div className="text-sm text-red-600">Cancelled</div>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <div className="text-3xl font-bold text-amber-600">{bookingStats.upcoming || 0}</div>
              <div className="text-sm text-amber-600">Upcoming</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-3xl font-bold text-purple-600">{bookingStats.reminders_5hr_sent || 0}</div>
              <div className="text-sm text-purple-600">5hr Reminders</div>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600">{bookingStats.reminders_1hr_sent || 0}</div>
              <div className="text-sm text-indigo-600">1hr Reminders</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion Funnel */}
      <Card className="border-border-line">
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="mr-2 text-care-teal" size={20} />
            Conversion Funnel
          </CardTitle>
          <CardDescription>Track visitor journey from landing to booking</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            {/* Funnel Steps */}
            <div className="flex-1 space-y-4">
              {/* Step 1: Visitors */}
              <div className="relative">
                <div className="h-12 bg-gradient-to-r from-tech-blue to-tech-blue/70 rounded-lg flex items-center justify-between px-4">
                  <span className="text-white font-medium">Website Visitors</span>
                  <span className="text-white font-bold">{conversionFunnel.visitors || 0}</span>
                </div>
              </div>
              
              {/* Step 2: Pricing Page Views */}
              <div className="relative ml-4">
                <div className="h-12 bg-gradient-to-r from-care-teal to-care-teal/70 rounded-lg flex items-center justify-between px-4" style={{width: '90%'}}>
                  <span className="text-white font-medium">Pricing Page Views</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{conversionFunnel.pricing_views || 0}</span>
                    <span className="text-white/80 text-sm">({conversionFunnel.rates?.pricing_view_rate || 0}%)</span>
                  </div>
                </div>
              </div>
              
              {/* Step 3: Form Submissions */}
              <div className="relative ml-8">
                <div className="h-12 bg-gradient-to-r from-heritage-orange to-heritage-orange/70 rounded-lg flex items-center justify-between px-4" style={{width: '80%'}}>
                  <span className="text-white font-medium">Form Submissions</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{conversionFunnel.form_submissions || 0}</span>
                    <span className="text-white/80 text-sm">({conversionFunnel.rates?.form_submission_rate || 0}%)</span>
                  </div>
                </div>
              </div>
              
              {/* Step 4: Bookings */}
              <div className="relative ml-12">
                <div className="h-12 bg-gradient-to-r from-purple-600 to-purple-600/70 rounded-lg flex items-center justify-between px-4" style={{width: '70%'}}>
                  <span className="text-white font-medium">Strategy Call Booked</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{conversionFunnel.bookings || 0}</span>
                    <span className="text-white/80 text-sm">({conversionFunnel.rates?.booking_rate || 0}%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Pages */}
      {topPages.length > 0 && (
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="mr-2 text-purple-500" size={20} />
              Top Pages
            </CardTitle>
            <CardDescription>Most visited pages on your website</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topPages.map((page, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-text-primary">{page.page}</span>
                      <span className="text-sm text-text-secondary">{page.views} views</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                        style={{width: `${Math.min((page.views / (topPages[0]?.views || 1)) * 100, 100)}%`}}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Demo Requests */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Recent Demo Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.demos?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.demos.map((demo, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary">{demo.name}</div>
                      <div className="text-sm text-text-secondary">{demo.company}</div>
                    </div>
                    <div className="text-xs text-text-secondary">
                      {new Date(demo.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-text-secondary py-4">No demo requests yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Bookings */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Recent Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.bookings?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.bookings.map((booking, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary">{booking.name}</div>
                      <div className="text-sm text-text-secondary">{booking.date} at {booking.time}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-text-secondary py-4">No bookings yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Contacts */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Recent Contact Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.contacts?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.contacts.map((contact, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary">{contact.name}</div>
                      <div className="text-sm text-text-secondary truncate max-w-[200px]">{contact.subject}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      contact.status === 'unread' ? 'bg-tech-blue/10 text-tech-blue' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {contact.status || 'unread'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-text-secondary py-4">No messages yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent AI Demo Leads */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Recent AI Demo Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.ai_leads?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.ai_leads.map((lead, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary">{lead.name}</div>
                      <div className="text-sm text-text-secondary">{lead.company}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-care-teal/10 text-care-teal capitalize">
                      {lead.feature_interest || 'general'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-text-secondary py-4">No AI demo leads yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
